import { Mail, Phone, ChevronRight } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxoYW5kcyUyMHdyaXRpbmclMjBqb3VybmFsJTIwc29mdCUyMGxpZ2h0fGVufDB8fHx8MTc4OTIwNjExNXww&ixlib=rb-4.1.0&q=85";

const publics = [
  "Enfants et adolescents",
  "Adultes",
  "Personnes âgées",
  "Personnes accompagnées en institution ou structure médico-sociale",
  "Groupes de femmes",
];

const bienfaits = [
  "Mettre des mots sur ce que l'on ressent",
  "Explorer son histoire et ses expériences",
  "Exprimer ce qui est parfois difficile à dire",
  "Développer sa créativité",
  "Prendre un temps pour soi",
  "Créer du lien avec les autres",
];

export default function Ateliers() {
  return (
    <div data-testid="ateliers-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-16 grid md:grid-cols-12 gap-10 items-center fade-in">
        <div className="md:col-span-6">
          <p className="font-hand text-2xl text-[#A3876A] mb-4">Accompagner autrement, par les mots</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight">
            Ateliers <em className="italic">d'écriture</em>
          </h1>
          <p className="text-lg text-[#4A4036] leading-relaxed mb-6">
            J'accompagne les personnes à travers des ateliers d'écriture pensés comme des espaces d'expression, de créativité et d'exploration de soi.
          </p>
          <p className="text-[#4A4036] leading-relaxed max-w-xl italic">
            Éducatrice spécialisée depuis 2015, j'ai choisi de mettre mon expérience de l'accompagnement au service d'une autre approche : celle des mots et de l'écriture.
          </p>
        </div>
        <div className="md:col-span-6">
          <img src={IMG} alt="Atelier d'écriture, mains posant les mots sur un carnet" className="w-full h-[440px] object-cover"/>
        </div>
      </section>

      {/* Accessibles à tous */}
      <section className="bg-[#F5F0E6] py-20 border-y border-[#E8DCC8]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <p className="font-hand text-xl text-[#A3876A] mb-3">Pour tout le monde</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Des ateliers accessibles à tous</h2>
            <p className="text-[#4A4036] leading-relaxed mb-4">
              Pas besoin d'être écrivain, ni même d'aimer écrire.
            </p>
            <p className="text-[#4A4036] leading-relaxed">
              À partir de consignes simples et de thématiques variées, chacun est invité à écrire librement, à son rythme, <em className="italic">sans jugement</em> et <em className="italic">sans recherche de performance</em>.
            </p>
          </div>
          <div>
            <p className="font-hand text-xl text-[#A3876A] mb-3">L'écriture permet de…</p>
            <ul className="space-y-3">
              {bienfaits.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-[#4A4036]">
                  <ChevronRight size={16} className="text-[#A3876A] mt-1 flex-shrink-0"/>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Écriture expressive */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-hand text-xl text-[#A3876A] mb-3">Une approche par l'écriture expressive</p>
          <h2 className="font-serif text-3xl md:text-4xl mb-8">Un cadre sécurisant et bienveillant</h2>
          <p className="text-[#4A4036] leading-relaxed mb-6 text-left md:text-center max-w-3xl mx-auto">
            Mes ateliers s'inscrivent dans la démarche de <strong>l'écriture expressive</strong>, sans se substituer à un accompagnement psychologique ou médical.
          </p>
          <p className="text-[#4A4036] leading-relaxed text-left md:text-center max-w-3xl mx-auto">
            Je crée un cadre sécurisant et bienveillant dans lequel chacun reste libre de ce qu'il souhaite écrire — et de ce qu'il souhaite partager.
          </p>
        </div>
      </section>

      {/* Pour les structures */}
      <section className="bg-[#F5F0E6] py-20 border-y border-[#E8DCC8]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-12">
            <p className="font-hand text-xl text-[#A3876A] mb-3">Pour les structures et les institutions</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-6">J'interviens auprès de tous les publics</h2>
            <p className="text-[#4A4036] leading-relaxed">
              Les ateliers peuvent être <strong>ponctuels</strong> ou proposés sous forme de <strong>cycles</strong>, et sont toujours adaptés au public, aux besoins et au projet de la structure.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {publics.map((p, i) => (
              <div key={i} className="bg-[#FDFBF7] p-5 border border-[#E8DCC8] flex items-start gap-3">
                <ChevronRight size={16} className="text-[#A3876A] mt-1 flex-shrink-0"/>
                <span className="text-[#4A4036]">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Un atelier peut devenir */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-hand text-xl text-[#A3876A] mb-3">Un atelier peut devenir…</p>
          <div className="grid sm:grid-cols-2 gap-6 mt-10 text-left">
            <div className="p-6 border-l-2 border-[#A3876A]">
              <p className="font-serif text-2xl mb-2">Un espace pour s'exprimer</p>
            </div>
            <div className="p-6 border-l-2 border-[#A3876A]">
              <p className="font-serif text-2xl mb-2">Un temps pour soi</p>
            </div>
            <div className="p-6 border-l-2 border-[#A3876A]">
              <p className="font-serif text-2xl mb-2">Une manière de créer du lien</p>
            </div>
            <div className="p-6 border-l-2 border-[#A3876A]">
              <p className="font-serif text-2xl mb-2">Une autre façon d'accompagner</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-[#2C2621] text-[#F5F0E6] py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-hand text-2xl text-[#A3876A] mb-4">Échangeons</p>
          <h2 className="font-serif text-3xl md:text-4xl mb-6">Vous souhaitez proposer un atelier dans votre structure ?</h2>
          <p className="text-[#D4C7B3] leading-relaxed mb-10 italic">
            Écrivez-moi ou appelez-moi, on parle de votre projet ensemble.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              data-testid="ateliers-mail-btn"
              href="mailto:amelie@lesrdvdelecriture.com?subject=Projet%20d%27atelier%20d%27%C3%A9criture"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#A3876A] text-white hover:bg-[#8f7457] transition-colors"
            >
              <Mail size={18}/> Écrire un mail
            </a>
            <a
              data-testid="ateliers-phone-btn"
              href="tel:+33761696875"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border border-[#A3876A] text-[#F5F0E6] hover:bg-[#A3876A]/20 transition-colors"
            >
              <Phone size={18}/> 07 61 69 68 75
            </a>
          </div>
          <p className="mt-8 text-sm text-[#D4C7B3]">
            amelie@lesrdvdelecriture.com
          </p>
        </div>
      </section>
    </div>
  );
}
